import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Box, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useSearchParams } from "react-router-dom";
import { properties } from "@/lib/properties";
import PropertyFilters, { PropertyTypeFilter, typeOptions, MAX_PRICE, parsePrice } from "@/components/PropertyFilters";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ease = [0.22, 1, 0.36, 1] as const;

const ITEMS_PER_PAGE = 6;

type ViewMode = "active" | "reserved" | "sold";


const PropertyGrid = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const soldParam = searchParams.get("sold");
  const [view, setView] = useState<ViewMode>(soldParam === "true" ? "sold" : "active");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [typeFilter, setTypeFilter] = useState<PropertyTypeFilter>("all");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, MAX_PRICE]);
  const [videoModal, setVideoModal] = useState<{ youtubeId: string; title: string } | null>(null);
  const [matterportModal, setMatterportModal] = useState<{ url: string; title: string } | null>(null);

  // React to ?sold=true/false changes (e.g. clicking nav link while already on home),
  // then clean the param from URL and scroll to the section.
  useEffect(() => {
    if (soldParam === "true" || soldParam === "false") {
      setView(soldParam === "true" ? "sold" : "active");
      setVisibleCount(ITEMS_PER_PAGE);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("sold");
      setSearchParams(newParams, { replace: true });
      requestAnimationFrame(() => {
        document.getElementById("nabidka")?.scrollIntoView({ behavior: "smooth" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soldParam]);


  const hasActiveFilters = typeFilter !== "all" || priceRange[0] > 0 || priceRange[1] < MAX_PRICE;

  const resetFilters = () => {
    setTypeFilter("all");
    setPriceRange([0, MAX_PRICE]);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const filtered = properties.filter(p => {
    // hidden properties are never listed in the grid
    if (p.hidden) return false;

    // view mode
    if (view === "sold") {
      if (!p.sold) return false;
    } else if (view === "reserved") {
      if (p.sold || !p.reserved) return false;
    } else {
      if (p.sold) return false;
    }


    // type filter
    if (typeFilter !== "all") {
      const match = typeOptions.find(o => o.value === typeFilter);
      if (match && !match.matchTypes.includes(p.type)) return false;
    }

    // price filter
    const price = parsePrice(p.price);
    if (price !== null) {
      if (price < priceRange[0]) return false;
      if (priceRange[1] < MAX_PRICE && price > priceRange[1]) return false;
    }

    return true;
  }).sort((a, b) => {
    // Dolní Měcholupy are the promoted current offer and must stay first.
    if (view === "active") {
      if (a.id === 124) return -1;
      if (b.id === 124) return 1;
    }

    if (a.sold && b.sold) {
      const aDate = a.soldAt ? new Date(a.soldAt).getTime() : 0;
      const bDate = b.soldAt ? new Date(b.soldAt).getTime() : 0;
      return bDate - aDate;
    }

    // Reserved properties go to the end (among non-sold)
    const aReserved = a.reserved ? 1 : 0;
    const bReserved = b.reserved ? 1 : 0;
    return aReserved - bReserved;
  });

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const isExpanded = visibleCount > ITEMS_PER_PAGE;

  const renderCard = (property: (typeof properties)[number], i: number) => (
    <motion.div
      key={property.id}
      layout
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
      transition={{ duration: 0.6, delay: (i % ITEMS_PER_PAGE) * 0.08, ease }}
      whileHover={{ y: -8, transition: { duration: 0.3, ease: "easeOut" } }}
    >
      <Link
        to={`/nemovitost/${property.slug}`}
        className={`glass-card-hover group cursor-pointer overflow-hidden block ${property.reserved ? 'ring-2 ring-amber-500 rounded-2xl' : ''}`}
      >
        <div className="relative overflow-hidden rounded-t-2xl">
          <motion.img
            src={property.image}
            alt={property.title}
            className="w-full h-64 object-cover"
            loading="lazy"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.7, ease }}
          />
          <div className="absolute top-4 left-4 flex gap-2">
            {property.features.map((f, fi) => {
              const isVideo = f === "Video-prohlídka";
              const isScan = f === "3D Scan";
              const video = property.videos?.[0];
              const canClick =
                (isVideo && video) || (isScan && property.matterportUrl);

              return (
                <motion.button
                  key={f}
                  type="button"
                  onClick={(e) => {
                    if (!canClick) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (isVideo && video) {
                      setVideoModal({ youtubeId: video.youtubeId, title: video.title || property.title });
                    } else if (isScan && property.matterportUrl) {
                      setMatterportModal({ url: property.matterportUrl, title: property.title });
                    }
                  }}
                  className={`badge-feature ${canClick ? "cursor-pointer hover:brightness-110 hover:scale-105 active:scale-95 transition-transform" : ""}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + fi * 0.1 }}
                >
                  {isVideo && <Video className="h-3 w-3" />}
                  {isScan && <Box className="h-3 w-3" />}
                  {f}
                </motion.button>
              );
            })}
          </div>
          {property.sold && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 bg-foreground/40 flex items-center justify-center"
            >
              <span className="badge-sold text-base px-6 py-2 font-bold">Prodáno</span>
            </motion.div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
              property.sold
                ? 'bg-red-500/10 text-red-600'
                : property.reserved
                  ? 'bg-amber-500/10 text-amber-600'
                  : 'bg-emerald-500/10 text-emerald-600'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                property.sold
                  ? 'bg-red-500'
                  : property.reserved
                    ? 'bg-amber-500 animate-pulse'
                    : 'bg-emerald-500'
              }`} />
              {property.sold ? 'Prodáno' : property.reserved ? 'Rezervováno' : 'K dispozici'}
            </span>
          </div>
          <h3 className="font-heading font-semibold text-lg text-foreground mb-1 group-hover:text-primary transition-colors duration-300">
            {property.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">{property.location} · {property.size}</p>
          <div className="flex items-center justify-between">
            <span className="text-xl font-heading font-bold text-foreground">{((property.sold || property.reserved) && !property.showPrice) ? "" : property.price}</span>
            <motion.div
              whileHover={{ scale: 1.2, rotate: 45 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
            >
              <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
            </motion.div>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  return (
    <section id="nabidka" className="section-padding bg-gradient-to-b from-muted/30 to-background">
      <div className="container-luxury">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease }}
          >
            <span className="badge-remax mb-4">Aktuální nabídka</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
              Nemovitosti v prodeji
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2, ease }}
            className="flex flex-wrap gap-3"
          >
            <Button
              variant="outline"
              onClick={() => { setView(view === "reserved" ? "active" : "reserved"); setVisibleCount(ITEMS_PER_PAGE); }}
              className={`rounded-xl border-border transition-all duration-300 ${view === "reserved" ? "bg-amber-500/10 text-amber-600 border-amber-500" : "hover:bg-secondary"}`}
            >
              {view === "reserved" ? "Skrýt rezervované" : "Zobrazit rezervované"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setView(view === "sold" ? "active" : "sold"); setVisibleCount(ITEMS_PER_PAGE); }}
              className={`rounded-xl border-border transition-all duration-300 ${view === "sold" ? "bg-red-500/10 text-red-600 border-red-500" : "hover:bg-secondary"}`}
            >
              {view === "sold" ? "Skrýt prodané" : "Zobrazit prodané"}
            </Button>
          </motion.div>

        </div>

        {/* Filters */}
        <PropertyFilters
          typeFilter={typeFilter}
          onTypeChange={(t) => { setTypeFilter(t); setVisibleCount(ITEMS_PER_PAGE); }}
          priceRange={priceRange}
          onPriceChange={(r) => { setPriceRange(r); setVisibleCount(ITEMS_PER_PAGE); }}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <p className="text-muted-foreground text-lg">Žádné nemovitosti neodpovídají zvoleným filtrům.</p>
            <Button variant="outline" onClick={resetFilters} className="mt-4 rounded-xl">
              Zrušit filtry
            </Button>
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {visible.map(renderCard)}
            </AnimatePresence>
          </div>
        )}

        {/* Show more / Show less */}
        {(hasMore || isExpanded) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="flex justify-center gap-3 mt-12"
          >
            {hasMore && (
              <Button
                onClick={() => setVisibleCount(v => v + ITEMS_PER_PAGE)}
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 px-8"
              >
                Zobrazit další ({Math.min(ITEMS_PER_PAGE, filtered.length - visibleCount)})
              </Button>
            )}
            {isExpanded && (
              <Button
                variant="outline"
                onClick={() => {
                  setVisibleCount(ITEMS_PER_PAGE);
                  document.getElementById("nabidka")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-xl border-border hover:bg-secondary"
              >
                Skrýt
              </Button>
            )}
          </motion.div>
        )}
      </div>

      {/* Video modal */}
      <Dialog open={!!videoModal} onOpenChange={(open) => !open && setVideoModal(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden border-0 bg-black">
          <DialogTitle className="sr-only">{videoModal?.title || "Video"}</DialogTitle>
          {videoModal && (
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoModal.youtubeId}?autoplay=1&rel=0`}
                title={videoModal.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Matterport 3D modal */}
      <Dialog open={!!matterportModal} onOpenChange={(open) => !open && setMatterportModal(null)}>
        <DialogContent className="max-w-6xl w-[95vw] p-0 overflow-hidden border-0 bg-black">
          <DialogTitle className="sr-only">{matterportModal?.title || "3D prohlídka"}</DialogTitle>
          {matterportModal && (
            <div className="aspect-video w-full">
              <iframe
                src={matterportModal.url}
                title={matterportModal.title}
                className="w-full h-full"
                allow="xr-spatial-tracking; fullscreen"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PropertyGrid;
